import axios from 'axios';

// Use relative URL in production, localhost in development
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : (process.env.REACT_APP_API_URL || 'http://localhost:8000/api');

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
            refresh: refreshToken,
          });

          const { access } = response.data;
          localStorage.setItem('access_token', access);

          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login/', { email, password }),
  logout: (refresh) => api.post('/auth/logout/', { refresh }),
  refreshToken: (refresh) => api.post('/auth/token/refresh/', { refresh }),
  getCurrentUser: () => api.get('/auth/me/'),
  changePassword: (data) => api.post('/auth/users/change_password/', data),
  forgotPassword: (email) => api.post('/auth/forgot-password/', { email }),
  resetPassword: (data) => api.post('/auth/reset-password/', data),
};

// Users API (admin users)
export const usersAPI = {
  list: (params) => api.get('/auth/users/', { params }),
  get: (id) => api.get(`/auth/users/${id}/`),
  create: (data) => api.post('/auth/users/', data),
  update: (id, data) => api.patch(`/auth/users/${id}/`, data),
  delete: (id) => api.delete(`/auth/users/${id}/`),
};

// Customers API
export const customersAPI = {
  list: (params) => api.get('/customers/', { params }),
  get: (id) => api.get(`/customers/${id}/`),
  create: (data) => api.post('/customers/', data),
  update: (id, data) => api.patch(`/customers/${id}/`, data),
  delete: (id) => api.delete(`/customers/${id}/`),
  getLicenses: (id) => api.get(`/customers/${id}/licenses/`),
};

// Licenses API
export const licensesAPI = {
  dashboard: () => api.get('/licenses/dashboard/'),
  list: (params) => api.get('/licenses/', { params }),
  get: (id) => api.get(`/licenses/${id}/`),
  create: (data) => api.post('/licenses/', data),
  update: (id, data) => api.patch(`/licenses/${id}/`, data),
  delete: (id) => api.delete(`/licenses/${id}/`),
  revoke: (id, reason) => api.post(`/licenses/${id}/revoke/`, { reason }),
  extend: (id, days, sendEmail = true, customMessage = '') => api.post(`/licenses/${id}/extend/`, { days, send_email: sendEmail, custom_message: customMessage }),
  generateKey: (id, machineId) => api.post(`/licenses/${id}/generate_key/`, { machine_id: machineId }),
  download: (id) => api.get(`/licenses/${id}/download/`),
  getActivations: (id) => api.get(`/licenses/${id}/activations/`),
  getReminders: (id) => api.get(`/licenses/${id}/reminders/`),
  sendReminder: (id, data) => api.post(`/licenses/${id}/send_reminder/`, data),
  sendThankYou: (id) => api.post(`/licenses/${id}/send_thank_you/`),
  emailLicense: (id) => api.post(`/licenses/${id}/email_license/`),
};

// Activity Logs API
export const activityAPI = {
  list: (params) => api.get('/auth/activity-logs/', { params }),
};

// Support Messages API
export const supportAPI = {
  // Public endpoint (no auth required)
  submit: (formData) => api.post('/auth/contact/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Admin endpoints
  list: (params) => api.get('/auth/support-messages/', { params }),
  get: (id) => api.get(`/auth/support-messages/${id}/`),
  update: (id, data) => api.patch(`/auth/support-messages/${id}/`, data),
  delete: (id) => api.delete(`/auth/support-messages/${id}/`),
  markRead: (id) => api.post(`/auth/support-messages/${id}/mark_read/`),
  markReplied: (id) => api.post(`/auth/support-messages/${id}/mark_replied/`),
  close: (id) => api.post(`/auth/support-messages/${id}/close/`),
  reply: (id, data) => api.post(`/auth/support-messages/${id}/reply/`, data),
  stats: () => api.get('/auth/support-messages/stats/'),
};

// API Keys API (Admin only) - Google Keys
export const apiKeysAPI = {
  list: (params) => api.get('/apikeys/', { params }),
  get: (id) => api.get(`/apikeys/${id}/`),
  create: (data) => api.post('/apikeys/', data),
  update: (id, data) => api.patch(`/apikeys/${id}/`, data),
  delete: (id) => api.delete(`/apikeys/${id}/`),
  validate: (id) => api.post(`/apikeys/${id}/validate/`),
  clearCooldown: (id) => api.post(`/apikeys/${id}/clear_cooldown/`),
  stats: () => api.get('/apikeys/stats/'),
};

// Anthropic (Claude) API Keys API
export const anthropicKeysAPI = {
  list: (params) => api.get('/apikeys/anthropic/', { params }),
  get: (id) => api.get(`/apikeys/anthropic/${id}/`),
  create: (data) => api.post('/apikeys/anthropic/', data),
  update: (id, data) => api.patch(`/apikeys/anthropic/${id}/`, data),
  delete: (id) => api.delete(`/apikeys/anthropic/${id}/`),
  validate: (id) => api.post(`/apikeys/anthropic/${id}/validate/`),
  clearCooldown: (id) => api.post(`/apikeys/anthropic/${id}/clear_cooldown/`),
  stats: () => api.get('/apikeys/anthropic/stats/'),
};

// OpenAI API Keys API
export const openaiKeysAPI = {
  list: (params) => api.get('/apikeys/openai/', { params }),
  get: (id) => api.get(`/apikeys/openai/${id}/`),
  create: (data) => api.post('/apikeys/openai/', data),
  update: (id, data) => api.patch(`/apikeys/openai/${id}/`, data),
  delete: (id) => api.delete(`/apikeys/openai/${id}/`),
  validate: (id) => api.post(`/apikeys/openai/${id}/validate/`),
  clearCooldown: (id) => api.post(`/apikeys/openai/${id}/clear_cooldown/`),
  stats: () => api.get('/apikeys/openai/stats/'),
};

// Customer AI Settings API
export const customerAISettingsAPI = {
  get: (customerId) => api.get(`/customers/${customerId}/ai-settings/`),
  update: (customerId, data) => api.patch(`/customers/${customerId}/ai-settings/`, data),
};

// AI Credits API
export const aiCreditsAPI = {
  list: (params) => api.get('/ai-credits/', { params }),
  get: (licenseId) => api.get(`/ai-credits/${licenseId}/`),
  addCredits: (licenseId, data) => api.post(`/ai-credits/${licenseId}/add_credits/`, data),
  stats: () => api.get('/ai-credits/stats/'),
  initializeAll: () => api.post('/ai-credits/initialize_all/'),
  // NR-357: monthly free quota — per-license override + global default.
  setQuota: (licenseId, monthlyQuotaOverride) =>
    api.post(`/ai-credits/${licenseId}/set_quota/`, { monthly_quota_override: monthlyQuotaOverride }),
  getGlobalQuota: () => api.get('/ai-credits/global_quota/'),
  setGlobalQuota: (monthlyQuota) => api.post('/ai-credits/global_quota/', { monthly_quota: monthlyQuota }),
};

// AI Usage API
export const aiUsageAPI = {
  list: (params) => api.get('/ai-usage/', { params }),
  get: (id) => api.get(`/ai-usage/${id}/`),
};

// Credit Transactions API
export const creditTransactionsAPI = {
  list: (params) => api.get('/credit-transactions/', { params }),
};

// Releases API (Admin only)
export const releasesAPI = {
  list: (params) => api.get('/releases/', { params }),
  get: (id) => api.get(`/releases/${id}/`),
  create: (data) => api.post('/releases/', data),
  update: (id, data) => api.patch(`/releases/${id}/`, data),
  delete: (id) => api.delete(`/releases/${id}/`),
  uploadFile: (id, formData, onUploadProgress) => api.post(`/releases/${id}/upload_file/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress
  }),
  // Direct S3 upload (faster - bypasses server)
  getUploadUrl: (id, fileName, fileType) => api.post(`/releases/${id}/get_upload_url/`, {
    file_name: fileName,
    file_type: fileType
  }),
  confirmUpload: (id, data) => api.post(`/releases/${id}/confirm_upload/`, data),
  sendNotification: (id, data) => api.post(`/releases/${id}/send_notification/`, data),
  getNotifications: (id) => api.get(`/releases/${id}/notifications/`),
  getCustomerCounts: () => api.get('/releases/customer_counts/'),
  getDownloadUrl: (id) => api.get(`/releases/${id}/download_url/`),
  publish: (id) => api.post(`/releases/${id}/publish/`),
  unpublish: (id) => api.post(`/releases/${id}/unpublish/`),
};

// Blog API
export const blogAPI = {
  list: (params) => api.get('/blog/', { params }),
  get: (id) => api.get(`/blog/${id}/`),
  create: (data) => api.post('/blog/', data),
  update: (id, data) => api.patch(`/blog/${id}/`, data),
  delete: (id) => api.delete(`/blog/${id}/`),
  uploadMedia: (formData) => api.post('/blog/upload_media/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Public endpoints
  publicList: () => api.get('/blog/public/'),
  publicGet: (slug) => api.get(`/blog/public/${slug}/`),
};

// Tutorials API
export const tutorialsAPI = {
  list: (params) => api.get('/tutorials/', { params }),
  get: (id) => api.get(`/tutorials/${id}/`),
  create: (data) => api.post('/tutorials/', data),
  update: (id, data) => api.patch(`/tutorials/${id}/`, data),
  delete: (id) => api.delete(`/tutorials/${id}/`),
  uploadVideo: (formData, onUploadProgress) => api.post('/tutorials/upload_video/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress
  }),
  uploadThumbnail: (formData) => api.post('/tutorials/upload_thumbnail/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  bySection: (section) => api.get('/tutorials/by_section/', { params: section ? { section } : {} }),
};

export default api;
